import { FC, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ArrowIcon } from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { FadeIn } from '../../components/motion/index';
import { PaginationController } from '../../components/pagination-controller/index';
import { Radio, RadioGroup } from '../../components/radio-group/index';
import {
  Company,
  SnapshotHooks,
  useFakePagination,
  useLocale,
  useNavigate,
  usePagination,
} from '../../hooks/index';
import { capTableStore } from '../../storage/local/index';
import { stakeholderTypeTranslation } from '../../translations/stakeholder-translation.util';
import { PageRoute } from '../../types/page.types';
import { DetailsSortVariant, detailsSortVariantTitleMap } from '../../types/snapshot.types';
import { CapTableMobilePagePlaceholder } from './components/cap-table-loading-placeholders';
import { DetailsItem } from './details-item';
import { sortStakeholdersFoundersFirst } from './utils/sort-cap-table-rows.util';
import { latestSnapshotId } from './variables';

export const CapTableMobileDetailsPage: FC = () => {
  const [selectedDetailsSortVariant, setDetailsSortVariant] = useState<DetailsSortVariant>(
    DetailsSortVariant.BY_STAKEHOLDER,
  );
  const { id } = useParams();

  const [currentPage, setCurrentPage] = usePagination('cap-table-details');
  const { selectedCompanyId } = Company.useSelected();

  const { isLoading, snapshots } = SnapshotHooks.useSnapshots({ companyId: selectedCompanyId });
  const { snapshot: currentSnapshot, isLoading: isLoadingCurrent } = SnapshotHooks.useCurrent({
    companyId: selectedCompanyId,
  });

  const { snapshot: snapshotByDate, isLoading: isLoadingSnapshotByDate } =
    SnapshotHooks.useSnapshotByDate({
      companyId: selectedCompanyId,
      date: capTableStore.get()?.customCapTableDate,
    });

  // List rows are slim (no full stats) — resolve the route's valuation/incorporation id to a
  // snapshot id and fetch the full row by id.
  const resolvedListRowId =
    latestSnapshotId === id || capTableStore.get()?.customCapTableDate
      ? undefined
      : (snapshots.find(({ valuation }) => valuation?.id === id) ??
          snapshots.find(({ companyIncorporation }) => companyIncorporation?.id === id))?.id;

  const { snapshot: snapshotById, isLoading: isLoadingSnapshotById } = SnapshotHooks.useSnapshot({
    companyId: selectedCompanyId,
    snapshotId: resolvedListRowId ?? '',
  });

  const snapshot =
    latestSnapshotId === id
      ? currentSnapshot
      : capTableStore.get()?.customCapTableDate
        ? snapshotByDate
        : snapshotById;

  const stakeholdersWithShares = useMemo(
    () =>
      sortStakeholdersFoundersFirst(
        snapshot?.stats?.stakeholders.filter(
          (stakeholder) =>
            stakeholder.shareClasses.reduce((sharesTotal, { issued }) => sharesTotal + issued, 0) >
            0,
        ) || [],
      ),
    [snapshot?.stats?.stakeholders],
  );

  const { data, totalPages } = useFakePagination({
    currentPage,
    take: 13,
    data: stakeholdersWithShares,
  });

  const { messagesLocale } = useLocale();

  const navigate = useNavigate(PageRoute.CAP_TABLE);

  const isPageLoading =
    isLoading ||
    isLoadingSnapshotByDate ||
    isLoadingSnapshotById ||
    (latestSnapshotId === id && isLoadingCurrent);

  if (isPageLoading) return <CapTableMobilePagePlaceholder />;

  return (
    <FadeIn>
      <div className="flex w-full flex-col gap-4 rounded-md bg-white px-3 py-4 shadow-sm">
        <div className="flex cursor-pointer gap-2 pt-4 text-xs font-[450] text-gray-400">
          <span className="text-brand-700" onClick={() => navigate()}>
            <AppFormattedMessage id="CAP_TABLE" />
          </span>
          <span>/</span>
          <span>
            <AppFormattedMessage id="POSITIONS" />
          </span>
        </div>
        <div className="flex items-center justify-between border-y border-gray-100 py-3">
          <div className="flex items-center gap-2">
            <Button
              className="centered size-9 rounded-full"
              onClick={() => navigate()}
              style={{
                boxShadow: '0px 0px 24px 0px #00000014',
                border: '1px solid #F2F2F2',
              }}
              styleType="NONE"
            >
              <ArrowIcon />
            </Button>
            <div className="flex flex-col">
              <span className="text-base font-semibold text-gray-700">
                <AppFormattedMessage id="CAP_TABLE" />
              </span>
              <span className="text-xs font-[450] text-gray-400">
                <AppFormattedMessage id="DETAILS" />
              </span>
            </div>
          </div>
        </div>
        <div className="rounded-3xl border border-gray-200 p-1">
          <RadioGroup
            onChange={setDetailsSortVariant}
            value={selectedDetailsSortVariant}
            wrapperClassName="w-full grow flex-1"
          >
            {detailsSortVariantTitleMap.map(([graphKey, graphTitle]) => (
              <Radio
                className="h-8 rounded-[32px] border-transparent bg-transparent !px-3 !py-2 text-xs font-[450] text-gray-700 data-[checked]:font-[550] data-[checked]:text-brand-25"
                key={graphKey}
                value={graphKey}
              >
                <span className="text-nowrap">
                  <AppFormattedMessage id={graphTitle} />
                </span>
              </Radio>
            ))}
          </RadioGroup>
        </div>

        {selectedDetailsSortVariant === DetailsSortVariant.BY_STAKEHOLDER && (
          <>
            {data?.map((stakeholder) => (
              <DetailsItem
                avatar
                key={stakeholder.id}
                {...stakeholder}
                description={messagesLocale[stakeholderTypeTranslation[stakeholder.type]]}
              />
            ))}
            <PaginationController
              currentPage={currentPage}
              onClick={setCurrentPage}
              totalPages={totalPages}
            />
          </>
        )}

        {selectedDetailsSortVariant === DetailsSortVariant.BY_SHARE_CLASS &&
          snapshot?.stats?.stakeholdersTypes
            ?.filter(
              (stakeholder) =>
                stakeholder.shareClasses.reduce(
                  (sharesTotal, { issued }) => sharesTotal + issued,
                  0,
                ) > 0,
            )
            ?.map(({ shareClasses, ...stakeholder }, i) =>
              shareClasses.map(({ name }) => (
                <DetailsItem
                  className="pb-0"
                  key={`${stakeholder.type}_${i}`}
                  {...stakeholder}
                  description={name}
                  name={messagesLocale[stakeholderTypeTranslation[stakeholder.type]]}
                  shareClasses={shareClasses.filter((shareClass) => shareClass.name === name)}
                >
                  <Button
                    className="flex w-full items-center justify-between rounded-none bg-brand-25 px-4 py-3"
                    onClick={() => id && navigate([id, stakeholder.type])}
                    styleType="NONE"
                  >
                    <span className="text-sm font-[450] text-brand-700">
                      <AppFormattedMessage
                        id="VIEW_ASSOCIATED_STAKEHOLDERS"
                        values={{
                          stakeholdersCount: snapshot?.stats.stakeholders
                            .filter(
                              (stakeholder) =>
                                stakeholder.shareClasses.reduce(
                                  (sharesTotal, { issued }) => sharesTotal + issued,
                                  0,
                                ) > 0,
                            )
                            .filter(({ type }) => type === stakeholder.type).length,
                        }}
                      />
                    </span>
                    <ArrowIcon className="rotate-180" iconColor="#2565C8" />
                  </Button>
                </DetailsItem>
              )),
            )}
      </div>
    </FadeIn>
  );
};
